import axios from 'axios';
import globals from "../Globals";
import Credentials from '../Credentials';
import EventBus from '../EventBus';

let lastTs = 0;

export default class requests {
    static toBase64(buf) {
        return btoa(String.fromCharCode(...new Uint8Array(buf)))
    }

    static fromBase64(b64) {
        const binary = atob(b64)
        const buf = new Uint8Array(binary.length)
        for (let i = 0; i < binary.length; i++) buf[i] = binary.charCodeAt(i)
        return buf.buffer
    }

    static hexToBytes(hex) {
        if (!hex || hex.length % 2 !== 0) throw new Error('Invalid hex');
        const out = new Uint8Array(hex.length / 2);
        for (let i = 0; i < out.length; i++) out[i] = parseInt(hex.substr(i * 2, 2), 16);
        return out;
    }

    static persistLogin() {
        try { localStorage.setItem("login", JSON.stringify(globals.loginData)); }
        catch (e) { /* quota / private mode */ }
    }

    static async importKeys() {
        globals.privateKey = await crypto.subtle.importKey("pkcs8",
            this.fromBase64(globals.loginData.privateKey),
            { name: "RSA-PSS", hash: "SHA-512" },
            false, ["sign"]);

        globals.privateKeyDecrypt = await crypto.subtle.importKey("pkcs8",
            this.fromBase64(globals.loginData.privateKey),
            { name: "RSA-OAEP", hash: "SHA-512" },
            false, ["decrypt"]);

        globals.backendPublicKey = await crypto.subtle.importKey("spki",
            this.fromBase64(Credentials.backendPublicKey),
            { name: "RSA-PSS", hash: "SHA-512" },
            false, ["verify"])

        globals.backendPublicKeyEncrypt = await crypto.subtle.importKey("spki",
            this.fromBase64(Credentials.backendPublicKey),
            { name: "RSA-OAEP", hash: "SHA-512" },
            false, ["encrypt"])
    }

    static async getKeys() {
        // Import keys
        if (globals.loginData.privateKey != null) {
            await this.importKeys();
            return;
        }

        // Generate new keys
        const keyPair = await crypto.subtle.generateKey({
            name: "RSA-PSS",
            modulusLength: 2048,
            publicExponent: new Uint8Array([1, 0, 1]),
            hash: "SHA-512"
        }, true, ["sign", "verify"]);

        // Export keys
        globals.loginData.privateKey = this.toBase64(await crypto.subtle.exportKey("pkcs8", keyPair.privateKey));
        globals.loginData.publicKey = this.toBase64(await crypto.subtle.exportKey("spki", keyPair.publicKey));
        globals.loginData.deviceId = null;

        this.persistLogin();
    }

    static async login(credential){
        await this.getKeys();
        await this.importKeys();
        let response;

        try {
            response = await axios.post(globals.serverUrl + Credentials.apiLogin, {
                credential: credential,
                deviceId: globals.loginData.deviceId,
                publicKey: globals.loginData.publicKey,
            }, { responseType: 'text' }); // backend returns "<signature>\n<json>"
        } catch (error) {
            return error.message;
        }

        // Split into signature and json
        const raw = response.data;
        const nl = raw.indexOf('\n');
        if (nl < 0) return 'Malformed response from server.';
        const signatureB64 = raw.slice(0, nl);
        const jsonStr = raw.slice(nl + 1);

        // Verify backend signature over the JSON using RSA-PSS SHA-512
        const enc = new TextEncoder();
        const verified = await crypto.subtle.verify(
            { name: 'RSA-PSS', saltLength: 64 },
            globals.backendPublicKey,
            this.fromBase64(signatureB64),
            enc.encode(jsonStr)
        );
        if (!verified) return 'Invalid signature from server.';

        const data = JSON.parse(jsonStr);
        if (!data.ok) return data.error || 'Login failed.';

        // Decrypt response auth: deviceId, accessToken, tokenExpireDate, aesKey
        const encryptedBytes = this.fromBase64(data.auth);
        const decrypted = await crypto.subtle.decrypt(
            { name: 'RSA-OAEP' },
            globals.privateKeyDecrypt,
            encryptedBytes
        );
        const decoder = new TextDecoder();
        const auth = decoder.decode(decrypted).split('\n');

        // Save login data
        globals.loginData.deviceId = auth[0];
        globals.loginData.accessToken = auth[1];
        globals.loginData.tokenExpireDate = auth[2];
        globals.loginData.aesKey = auth[3];
        globals.loginData.id = data.id;
        globals.loginData.name = data.name;
        globals.loginData.email = data.email;
        globals.loginData.titles = data.titles;
        this.persistLogin();

        return auth[1] ? true : 'New device: Ask another admin to authorize this device.';
    }

    static logout(unregisterDevice) {
        this.request("general logout", {"unregisterDevice": unregisterDevice});

        if (unregisterDevice) {
            localStorage.removeItem("login");
            globals.loginData = {};
        } else {
            globals.loginData.accessToken = null;
            globals.loginData.tokenExpireDate = null;
            this.persistLogin();
        }
    }

    // Monotonic per-tab timestamp. The backend answers "retry" to any request whose timestamp is <= the
    // device's last one (anti-replay), so two calls in the same millisecond would collide.
    static nextTs() {
        let ts = Date.now();
        if (ts <= lastTs) ts = lastTs + 1;
        lastTs = ts;
        return ts;
    }

    /**
     * request(action, args, options)
     *   options.retry    also retry transient transport failures (network / timeout / HTTP 5xx). Default false.
     *                    A "retry" answer from the backend is ALWAYS retried: it is issued before the action
     *                    executes (timestamp check), so re-sending is safe. Auth/business errors never retry.
     *   options.timeout  axios timeout in ms (default 30000).
     *   options.attempts max attempts (default 3), with jittered backoff.
     */
    static async request(action, args, options = {}) {
        const { retry = false, timeout = 30000, attempts = 3 } = options;
        for (let attempt = 1; ; attempt++) {
            try {
                return await this.requestOnce(action, args, timeout);
            } catch (error) {
                const transient = error.kind === 'retry' || (retry && (error.kind === 'network' || error.kind === 'http'));
                if (!transient || attempt >= attempts) throw error;
                await new Promise(r => setTimeout(r, 200 + Math.random() * 400 * attempt));
            }
        }
    }

    static fail(message, kind, extra) {
        const e = new Error(message);
        e.kind = kind;
        if (extra) Object.assign(e, extra);
        return e;
    }

    static async requestOnce(action, args, timeout) {
        // Build plaintext payload: [accessToken(bytes)][int64 timestamp][int32 fileSize][optional file]["<action>\n<body>"]
        const encoder = new TextEncoder();
        const deviceIdHex = globals.loginData.deviceId;
        const tokenB64 = globals.loginData.accessToken;
        const aesKeyB64 = globals.loginData.aesKey;
        if (!deviceIdHex || !tokenB64 || !aesKeyB64) throw this.fail('Not logged in', 'auth');

        const deviceIdBytes = requests.hexToBytes(deviceIdHex); // 12 bytes
        const tokenBytes = new Uint8Array(this.fromBase64(tokenB64));
        const aesKeyRaw = new Uint8Array(this.fromBase64(aesKeyB64));
        const aesKey = await crypto.subtle.importKey('raw', aesKeyRaw, { name: 'AES-CBC' }, false, ['encrypt', 'decrypt']);

        let fileBytes = null;
        let bodyStr = '';
        if (args instanceof File) {
            const buf = await args.arrayBuffer();
            fileBytes = new Uint8Array(buf);
            bodyStr = '';
        } else {
            bodyStr = JSON.stringify(args ?? {});
        }
        const gameId = globals.selectedTitle ?? '';
        const tailStr = gameId + '\n' + action + '\n' + bodyStr;
        const tailBytes = encoder.encode(tailStr);

        const fileSize = fileBytes ? fileBytes.length : 0;

        const plainLen = tokenBytes.length + 8 + 4 + fileSize + tailBytes.length;
        const plain = new Uint8Array(plainLen);
        let off = 0;
        plain.set(tokenBytes, off); off += tokenBytes.length;
        const dv = new DataView(plain.buffer);

        const now = this.nextTs();
        dv.setUint32(off, now >>> 0, true);            // low 32 bits, LE
        dv.setUint32(off + 4, Math.floor(now / 0x100000000), true); // high 32 bits, LE
        off += 8;

        dv.setInt32(off, fileSize, true); off += 4;
        if (fileSize > 0) { plain.set(fileBytes, off); off += fileSize; }
        plain.set(tailBytes, off);

        // Encrypt with AES-CBC using random IV
        const iv = crypto.getRandomValues(new Uint8Array(16));
        const ciphertext = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-CBC', iv }, aesKey, plain));

        // Build request: [12-byte deviceId][16-byte IV][ciphertext]
        const reqBuf = new Uint8Array(12 + 16 + ciphertext.length);
        reqBuf.set(deviceIdBytes, 0);
        reqBuf.set(iv, 12);
        reqBuf.set(ciphertext, 28);

        // Send request as binary and expect binary response: [IV][ciphertext]
        let response;
        try {
            response = await axios.post(globals.serverUrl + Credentials.apiEntryPoint, reqBuf, {
                headers: { 'Content-Type': 'application/octet-stream' },
                responseType: 'arraybuffer',
                timeout,
            });
        } catch (error) {
            if (error.response) {
                let text = '';
                try { text = new TextDecoder().decode(error.response.data).slice(0, 200); } catch (e) { /* binary */ }
                throw this.fail(`Server error ${error.response.status}${text ? ': ' + text : ''}`, 'http', { status: error.response.status });
            }
            throw this.fail(error.code === 'ECONNABORTED' ? 'Request timed out' : (error.message || 'Network error'), 'network');
        }

        const respBytes = new Uint8Array(response.data);
        if (respBytes.length < 16) throw this.fail('Malformed response', 'bad-response');
        const respIv = respBytes.slice(0, 16);
        const respCt = respBytes.slice(16);
        const plainResp = await crypto.subtle.decrypt({ name: 'AES-CBC', iv: respIv }, aesKey, respCt);
        const text = new TextDecoder().decode(plainResp);

        // Direct commands
        if (text === 'refresh_login') {
            if (globals.loginData.accessToken != null) EventBus.emit('logout', false);
            throw this.fail('Login Expired.', 'auth');
        }
        if (text === 'retry') throw this.fail('Please retry.', 'retry');

        // Display error messages
        if (!text || (text[0] !== '{' && text[0] !== '[')) {
            throw this.fail(text || 'Empty response', 'business');
        }

        // Return parsed JSON body
        try { return JSON.parse(text); }
        catch (e) { throw this.fail('Bad response from server', 'bad-response'); }
    }
}

// Debug handle for browser-driven tests (harmless in production).
if (typeof window !== 'undefined') window.__aurecasRequests = requests;
